import React, { useContext, useState } from "react";
import { Card, Button, ProgressBar } from "react-bootstrap";
import { Upload, Download, CheckCircle, BookOpen } from "lucide-react";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import GlobalContext from "../../context/GlobalContext";

import { ValidateImportUsers, SubmitImportUsers } from "../../api/UsersApi";

import swal from "../../services/swalConfig";

import { tr } from "../../i18n";
export default function ImportUsers() {
  const { user, setLoading } = useContext(GlobalContext);
  const [uploadedData, setUploadedData] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [importedFile, setImportedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleDownloadTemplate = () => {
    const fileUrl = "/templates/employee_template.xls"; // ← ganti template
    window.open(fileUrl, "_blank");
  };

  const handleFileUpload = async (file) => {
    const fileInput = document.getElementById("importFile");
    if (fileInput) fileInput.value = "";

    setUploadedData([]);
    setImportedFile(file);
    setUploadProgress(0);

    const companyId = user?.workspace?.company_id;
    if (!companyId) {
      alert(tr("users:error_company_id_not_found"));
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("company_id", companyId);

    try {
      setUploadProgress(1);

      const response = await ValidateImportUsers(formData, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          setUploadProgress(percentCompleted);
        },
      });

      if (response.status === "success") {
        setUploadedData(response.validated_data || []);
        setUploadProgress(100);
      } else {
        swal.fire({
          title: tr("shared:oops"),
          text: response.message,
          icon: "error",
        });
        setUploadedData([]);
        setUploadProgress(0);
      }
    } catch (error) {
      swal.fire({
        title: tr("shared:oops"),
        text: tr("users:unexpected_error_during_file_submission"),
        icon: "error",
      });
      setUploadedData([]);
      setUploadProgress(0);
    }
  };

  const handleProcessValid = async () => {
    // Ambil hanya data yang error nya kosong
    const validRows = uploadedData.filter((row) => row.error.length === 0);

    if (validRows.length === 0) {
      swal.fire({
        title: tr("shared:no_valid_data"),
        text: tr("shared:there_are_no_valid_rows_to"),
        icon: "warning",
      });
      return;
    }

    const companyId = user?.workspace?.company_id;
    if (!companyId) {
      swal.fire({
        title: tr("shared:company_not_found"),
        text: tr("users:missing_company_id"),
        icon: "error",
      });
      return;
    }

    setLoading(true);

    try {
      const payload = {
        company_id: companyId,
        records: validRows,
      };

      const response = await SubmitImportUsers(payload);
      const data = response;

      if (data.status != "success") {
        swal.fire({
          title: tr("shared:error"),
          text: data.message,
          icon: "error",
        });
        return;
      }

      swal.fire({
        title: tr("shared:success"),
        text: `${data.message}`,
        icon: "success",
      });

      setUploadedData([]);
      setImportedFile(null);
      setUploadProgress(0);
    } catch (err) {
      swal.fire({
        title: tr("shared:unexpected_error"),
        text: tr("shared:something_went_wrong"),
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files[0]);
      e.dataTransfer.clearData();
    }
  };

  const handleInputChange = (e) => {
    handleFileUpload(e.target.files[0]);
  };

  const validCount = uploadedData.filter(
    (row) => Array.isArray(row.error) && row.error.length === 0,
  ).length;

  const handleShowTutorial = () => {
    const d = driver({
      showProgress: true,
      steps: [
        {
          element: "#tutorial-download-template",
          popover: {
            title: tr("shared:step_1_download_template"),
            description:
              "Download the **Excel template** to ensure correct format.<br><br>" +
              "You also need templates to import employees:<br>" +
              "1. Create <b>Payroll Template</b> <a href='/payroll/form' target='_blank' style='color: #007dff;'>here</a><br>" +
              "2. Create <b>Schedule Template</b> <a href='/schedules/form' target='_blank' style='color: #007dff;'>here</a>",
          },
        },
        {
          element: "#tutorial-upload-area",
          popover: {
            title: tr("users:step_2_upload_file"),
            description:
              tr("users:upload_your_filled_employee_excel_file"),
          },
        },
        {
          element: "#tutorial-data-preview",
          popover: {
            title: tr("users:step_3_preview_data"),
            description:
              tr("users:check_errors_in_red_rows_only"),
          },
        },
        {
          element: "#tutorial-process-button",
          popover: {
            title: tr("users:step_4_process_data"),
            description: tr("users:click_to_submit_and_import_all"),
          },
        },
      ],
    });

    d.drive();
  };

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <Button
          variant="outline-secondary"
          onClick={handleShowTutorial}
          className="d-flex align-items-center gap-2"
        >
          <BookOpen size={16} /> {" "}{tr("shared:show_tutorial")}
        </Button>

        <Button
          variant="outline-primary"
          id="tutorial-download-template"
          onClick={handleDownloadTemplate}
          className="d-flex align-items-center gap-2"
        >
          <Download size={16} /> {" "}{tr("shared:download_template")}
        </Button>
      </div>

      {/* Upload Box */}
      <Card className="border-0 shadow-sm p-4 mb-4">
        <div
          id="tutorial-upload-area"
          className={`w-100 p-4 text-center border border-2 border-dashed rounded ${
            isDragging ? "border-primary bg-light" : ""
          }`}
          style={{ cursor: "pointer", borderStyle: "dashed" }}
          onClick={() => document.getElementById("importFile").click()}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <Upload size={40} className="text-primary mb-2" />
          <p className="fw-semibold mb-0">
            {tr("shared:click_here_to_upload_your_excel")}
          </p>
          <small className="text-muted">{tr("shared:supported_format_xls_xlsx")}</small>
        </div>

        <input
          type="file"
          accept=".xls,.xlsx"
          id="importFile"
          hidden
          onChange={handleInputChange}
        />
      </Card>

      {/* Preview Table */}
      <Card className="border-0 shadow-sm p-3" id="tutorial-data-preview">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="fw-bold m-0">{tr("shared:uploaded_data_preview")}</h6>

          <Button
            variant="primary"
            id="tutorial-process-button"
            disabled={validCount === 0}
            onClick={handleProcessValid}
            className="px-4"
          >
            {tr("shared:process_valid")}{validCount})
          </Button>
        </div>

        <div className="table-responsive">
          <table>
            <thead style={{ fontSize: "9pt" }}>
              <tr>
                <th>#</th>
                <th>{tr("shared:email")}</th>
                <th>{tr("shared:name")}</th>
                <th>{tr("users:position")}</th>
                <th>{tr("users:sallary")}</th>
                <th>{tr("common:breadcrumb.paid-leave")}</th>
                <th>{tr("common:menu.payroll")}</th>
                <th>{tr("common:breadcrumb.schedule")}</th>
                <th>{tr("users:spv")}</th>
                <th>{tr("users:join_on")}</th>
                <th>{tr("shared:message")}</th>
              </tr>
            </thead>

            <tbody style={{ fontSize: "9pt" }}>
              {uploadProgress > 0 && uploadProgress < 100 ? (
                <tr>
                  <td colSpan="11" className="text-center py-5">
                    <ProgressBar
                      now={100}
                      label={tr("shared:processing")}
                      animated
                      striped
                    />
                  </td>
                </tr>
              ) : uploadedData.length === 0 ? (
                <tr>
                  <td colSpan="10" className="text-center text-muted py-5">
                    {tr("shared:no_data_imported_yet")}
                  </td>
                </tr>
              ) : (
                uploadedData.map((row, index) => (
                  <tr key={index} className={row.error ? "table-danger" : ""}>
                    <td>{index + 1}</td>
                    <td>{row.email}</td>
                    <td>{row.full_name}</td>
                    <td>{row.position}</td>
                    <td>{row.sallary}</td>
                    <td>{row.paid_leave}</td>
                    <td>{row.template_payroll}</td>
                    <td>{row.template_schedule}</td>
                    <td>{row.spv}</td>
                    <td>{row.join_date}</td>
                    <td>
                      {row.error && row.error.length > 0 ? (
                        <ul
                          style={{ paddingLeft: "15px" }}
                          className="text-danger mb-0"
                        >
                          {row.error.map((e, i) => (
                            <li key={i}>{e}</li>
                          ))}
                        </ul>
                      ) : (
                        <div className="d-flex align-items-center gap-1">
                          <CheckCircle size={14} className="text-success" />
                          <span className="text-success text-sm">{tr("shared:valid")}</span>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
